/**
 * Settings > Developers: connect an assistant (the copy-paste installs),
 * personal API keys, OAuth connected apps, and the way into the docs.
 */
import { useEffect, useState } from "react";
import { BookOpen, Plug, Unplug } from "lucide-react";
import type { ConnectedAppDto } from "@hydra-notes/shared";
import { useAuth } from "@/data/auth";
import { useTaskStats } from "@/data/taskStats";
import { ApiKeysSection } from "@/tasks/ApiKeysSection";
import { Button } from "@/ui/Button";
import { CodeBlock } from "./CodeBlock";
import { useDocsDialog } from "./DocsDialog";
import { snippets, useDocs } from "./docs";
import s from "./developers.module.css";

type Method = "claude" | "json" | "rest";

export function DevelopersTab() {
  const signedIn = useAuth((a) => a.status === "signedIn");
  const { docs } = useDocs();
  const [method, setMethod] = useState<Method>("claude");
  const [apps, setApps] = useState<ConnectedAppDto[]>([]);
  const refreshKeys = useTaskStats((st) => st.refreshKeys);
  const keys = useTaskStats((st) => st.keys);

  const loadApps = () =>
    fetch("/api/oauth/apps", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { apps: [] }))
      .then((d: { apps: ConnectedAppDto[] }) => setApps(d.apps ?? []))
      .catch(() => {});

  useEffect(() => {
    if (!signedIn) return;
    void refreshKeys();
    void loadApps();
  }, [signedIn, refreshKeys]);

  const exampleKey = keys[0] ? `${keys[0].prefix}...` : "hn_your_key_here";
  const sn = snippets(docs, exampleKey);

  if (!signedIn) {
    return (
      <>
        <h2 className={s.sectionHead}>Developers</h2>
        <p className={s.hint}>Sign in to connect assistants, create API keys and manage connected apps.</p>
      </>
    );
  }

  return (
    <>
      <section className={s.section} data-developers-tab>
        <div className={s.sectionHead}>
          <h2>
            <Plug size={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Connect an assistant
          </h2>
          <Button variant="subtle" size="sm" onClick={() => useDocsDialog.getState().setOpen(true)}>
            <BookOpen size={13} /> Documentation
          </Button>
        </div>
        <p className={s.hint}>
          Claude and other assistants connect over MCP and work inside your live notes, tasks and cards. You watch them
          type, with their name on the caret. Sign-in happens in this app the first time; nothing to paste but the line
          below.
        </p>
        <div className={s.tabs} role="tablist">
          {(
            [
              ["claude", "Claude Code"],
              ["json", "Other MCP hosts"],
              ["rest", "Scripts and hosted models"],
            ] as [Method, string][]
          ).map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={method === id} className={[s.tab, method === id ? s.tabActive : ""].join(" ")} onClick={() => setMethod(id)}>
              {label}
            </button>
          ))}
        </div>
        {method === "claude" ? (
          <>
            <CodeBlock label="Run once in any terminal. Installs for your user, every project." code={sn.claudeOauth} />
            <ol className={s.steps}>
              <li>Type /mcp inside Claude Code and choose hydra-notes to sign in.</li>
              <li>Approve the request in the Hydra Notes tab that opens.</li>
              <li>Ask Claude to do something in your notes and watch it type.</li>
            </ol>
            <CodeBlock label="Prefer a key, for servers without a browser" code={sn.claudeKey} />
          </>
        ) : null}
        {method === "json" ? (
          <>
            <CodeBlock label="Claude Desktop, Cursor, Windsurf: hosts with OAuth need only the URL" code={sn.jsonOauth} />
            <CodeBlock label="Hosts without OAuth: add a key as a header" code={sn.jsonKey} />
          </>
        ) : null}
        {method === "rest" ? (
          <>
            <p className={s.hint}>
              Anything that speaks HTTPS can use the REST API with a key. Add an <code>X-Hydra-Actor</code> header to a
              write and it appears in the app under that name, exactly like an MCP model.
            </p>
            <CodeBlock label="Who am I" code={sn.rest} />
            <CodeBlock label="Add a task" code={sn.restTask} />
          </>
        ) : null}
      </section>

      <ApiKeysSection />

      <section className={s.section}>
        <div className={s.sectionHead}>
          <h2>
            <Unplug size={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Connected apps
          </h2>
        </div>
        {apps.length === 0 ? (
          <p className={s.hint}>No assistant has signed in with OAuth yet. Once one does, it appears here and can be disconnected.</p>
        ) : (
          apps.map((a) => (
            <div key={a.clientId} className={s.appRow}>
              <span className={s.appName}>
                {a.clientName}
                <span className={s.appHint}>
                  {a.scope === "write" ? "Read and write" : "Read only"} · connected {new Date(a.createdAt).toLocaleDateString()}
                  {a.lastUsedAt ? ` · last used ${new Date(a.lastUsedAt).toLocaleString()}` : ""}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  void fetch(`/api/oauth/apps/${a.clientId}`, { method: "DELETE", credentials: "same-origin" }).then(loadApps);
                }}
              >
                Disconnect
              </Button>
            </div>
          ))
        )}
      </section>
    </>
  );
}
